import { server } from "../../config/ConfigFirebase";
import { ROLES } from "../../constants";
import { debugLog } from "../../functions";

const handleUser = async (token, body) => {
  return await fetch("https://" + server + ".cloudfunctions.net/user-handle", {
    method: "post",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  });
};

export const getUser = async (token, userUID) => {
  try {
    const userBody = { action: "GET_USER", userUID };
    const response = await handleUser(token, userBody);
    const responseJSON = await response.json();
    if (responseJSON.result) {
      responseJSON.user.token = token;
      responseJSON.user = getUserPermissions(responseJSON.user);
      return responseJSON;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const getUserPermissions = (user) => {
  const permissions = new Set(user.permissions);
  user.roles?.forEach((role) => ROLES[role]?.forEach(permissions.add, permissions));
  user.permissions = Array.from(permissions);
  return user;
};

export const getReciversUser = async (token) => {
  try {
    const userBody = { action: "INTERNAL_RECEIVER" };
    let response = await handleUser(token, userBody);
    let responseJSON = await response.json();
    if (responseJSON.result) {
      return responseJSON;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};
export const getPeople = async (token) => {
  try {
    const peopleBody = { action: "EXTERNAL_RECEIVER" };
    let response = await handleUser(token, peopleBody);
    let responseJSON = await response.json();
    if (responseJSON.result) {
      return responseJSON;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};
export const addUser = async (bodyUser, user) => {
  try {
    const userBody = { action: "ADD_USER", ...bodyUser };
    let response = await handleUser(user.token, userBody);
    let responseJSON = await response.json();
    if (responseJSON.result) {
      return responseJSON;
    } else {
      return { result: false, error: responseJSON.error || null };
    }
  } catch (error) {
    debugLog(error);
    return { result: false, error: error.message };
  }
};
export const disableUser = async (userId, disabled, user) => {
  try {
    const userBody = { action: "DISABLE_USER", userId, disabled };
    let response = await handleUser(user.token, userBody);
    let responseJSON = await response.json();
    if (responseJSON.result) {
      return responseJSON;
    } else {
      return { result: false, error: responseJSON.error || null };
    }
  } catch (error) {
    debugLog(error);
    return { result: false, error: error.message };
  }
};
// ADMIN only, on another staff user: moves their Firebase Auth sign-in address
// and the record together. Returns { result, email } or { result: false, error }
// with the backend's code (EMAIL_IN_USE, SAME_EMAIL, INVALID_EMAIL,
// ENTITY_ACCOUNT, FORBIDDEN...).
export const changeUserEmail = async (userId, newEmail, user) => {
  try {
    const userBody = { action: "CHANGE_EMAIL", userId, newEmail };
    const response = await handleUser(user.token, userBody);
    const responseJSON = await response.json();
    if (responseJSON.result) return responseJSON;
    return { result: false, error: responseJSON.error || null };
  } catch (error) {
    debugLog(error);
    return { result: false, error: error.message };
  }
};
// ADMIN only, on a DISABLED staff user: moves their sign-in address aside so a
// new account can take it. Returns { result, email } (the address it moved
// to) or { result: false, error } (USER_NOT_DISABLED, ALREADY_RELEASED,
// ENTITY_ACCOUNT, NO_AUTH_ACCOUNT, FORBIDDEN...).
export const releaseUserEmail = async (userId, user) => {
  try {
    const response = await handleUser(user.token, { action: "RELEASE_EMAIL", userId });
    const responseJSON = await response.json();
    if (responseJSON.result) return responseJSON;
    return { result: false, error: responseJSON.error || null };
  } catch (error) {
    debugLog(error);
    return { result: false, error: error.message };
  }
};
// What was done to one user (userAudit), newest first. Its own request, made
// when a user is opened in the manager, so the history never rides along with
// the directory every staff member downloads. Returns an array, or null.
export const getUserAudit = async (userId, user) => {
  try {
    const response = await handleUser(user.token, { action: "GET_USER_AUDIT", userId });
    const responseJSON = await response.json();
    return responseJSON.result ? responseJSON.audit || [] : null;
  } catch (error) {
    debugLog(error);
    return null;
  }
};
export const addPeople = async (bodyPeople, user) => {
  try {
    const peopleBody = { action: "ADD_PEOPLE", ...bodyPeople };
    let response = await handleUser(user.token, peopleBody);
    let responseJSON = await response.json();    
    if (responseJSON.result) {
      return responseJSON;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};
export const updateUser = async (bodyUser, user) => {
  try {
    const userBody = { action: "UPDATE_USER", ...bodyUser };

    let response = await handleUser(user.token, userBody);
    let responseJSON = await response.json();
    if (responseJSON.result) {
      return responseJSON;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};

// Grants or revokes a single role on a user, keeping the stored permissions
// snapshot in sync the same way UserAdmin.handleToggleRole does (adds/removes the
// role's current perms). Idempotent — a no-op (and no network call) when the user
// is already in the desired state. `targetUser` must carry userId/roles/permissions
// (e.g. an object from state.user.users). `actor` supplies the auth token. Returns
// the updated user object, or throws on a failed write.
export const setUserRole = async (targetUser, role, shouldHave, actor) => {
  const has = (targetUser.roles || []).includes(role);
  if (shouldHave === has) return targetUser; // already in the desired state

  const roles = new Set(targetUser.roles || []);
  const permissions = new Set(targetUser.permissions || []);
  const rolePerms = ROLES[role] || [];
  if (shouldHave) {
    roles.add(role);
    rolePerms.forEach((p) => permissions.add(p));
  } else {
    roles.delete(role);
    // Solo se restan los permisos que NINGÚN otro rol vigente aporta: quitar
    // PQRSD_DEPENDENCY_SUPERVISOR a un PQRSD_OPERATOR borraba también los del
    // operario, porque el primero es un superconjunto del segundo. El backend
    // lee este array tal cual, así que el usuario quedaba sin permisos allí
    // mientras el frontend seguía derivándolos del rol y nada se veía raro.
    const kept = new Set(Array.from(roles).flatMap((r) => ROLES[r] || []));
    rolePerms.forEach((p) => {
      if (!kept.has(p)) permissions.delete(p);
    });
  }

  const nextRoles = Array.from(roles);
  const nextPermissions = Array.from(permissions);
  const res = await updateUser(
    { userId: targetUser.userId, roles: nextRoles, permissions: nextPermissions },
    actor
  );
  if (!res?.result) {
    throw new Error(`No fue posible actualizar el rol de ${targetUser.name || targetUser.userId}.`);
  }
  return { ...targetUser, roles: nextRoles, permissions: nextPermissions };
};
export const updatePeople = async (bodyPeople, user) => {
  try {
    const peopleBody = { action: "UPDATE_PEOPLE", ...bodyPeople };

    let response = await handleUser(user.token, peopleBody);
    let responseJSON = await response.json();
    if (responseJSON.result) {
      return responseJSON;
    } else {
      return null;
    }
  } catch (error) {
    debugLog(error);
    return null;
  }
};
